import React from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { CustomerStories as CustomerStoriesSection } from "@/components/site/GleapSections";

export default function CustomerStories() {
    const { openDemo } = useOutletContext();

    return (
        <div className="bg-warm">
            {/* HERO */}
            <section className="relative overflow-hidden pt-14 pb-16 md:pb-20 bg-hero-glow">
                <div className="container-x relative z-10">
                    <Reveal>
                        <Link
                            to="/use-cases"
                            className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-2 mb-8"
                        >
                            <i className="fa-solid fa-arrow-left" /> Back to Use Cases
                        </Link>
                    </Reveal>
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <Reveal delay={0.05}>
                                <div className="text-sm text-slate-500 mb-4">Customer stories</div>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h1 className="heading-hero text-4xl sm:text-5xl lg:text-6xl">
                                    Real deployments.
                                    <br />
                                    <span className="muted italic">Measurable outcomes.</span>
                                </h1>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
                                    From distance-learning counselling on voice to fintech credit
                                    resolution on WhatsApp — Nyra AI is already at work behind the
                                    brands millions of Indians trust every day.
                                </p>
                            </Reveal>
                            <Reveal delay={0.25}>
                                <div className="mt-8 flex flex-wrap gap-2">
                                    {[
                                        { label: "Distance Education", icon: "fa-solid fa-graduation-cap" },
                                        { label: "Fintech · Credit", icon: "fa-solid fa-chart-line" },
                                        { label: "Voice AI", icon: "fa-solid fa-microphone" },
                                        { label: "WhatsApp", icon: "fa-brands fa-whatsapp" },
                                    ].map((c) => (
                                        <span
                                            key={c.label}
                                            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600"
                                        >
                                            <i className={`${c.icon} text-[10px] text-slate-400`} />
                                            {c.label}
                                        </span>
                                    ))}
                                </div>
                            </Reveal>
                        </div>

                        {/* RIGHT — hero image (woman using phone) */}
                        <Reveal delay={0.3} className="lg:col-span-5">
                            <div className="relative h-[360px] md:h-[440px] w-full">
                                {/* Soft aurora backdrop */}
                                <div
                                    aria-hidden
                                    className="absolute -inset-4 rounded-[2.5rem] pointer-events-none"
                                    style={{
                                        background:
                                            "radial-gradient(60% 55% at 30% 30%, rgba(95,168,138,0.22) 0%, transparent 60%)," +
                                            "radial-gradient(50% 50% at 80% 80%, rgba(212,181,66,0.20) 0%, transparent 65%)",
                                    }}
                                />

                                {/* Image card */}
                                <div
                                    className="relative h-full w-full rounded-[2rem] overflow-hidden border border-white/60"
                                    style={{ boxShadow: "0 30px 70px -25px rgba(35,95,82,0.35)" }}
                                >
                                    <img
                                        src="https://static.prod-images.emergentagent.com/jobs/4da0b27b-01e9-4410-a5bb-7719ce6ad6e8/images/0daf399885ab22132820523e47dde110d5b90bc0381be97b809ded339f7b95cc.jpeg"
                                        alt="Customer using Nyra AI on WhatsApp"
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    {/* Subtle warm gradient tint to blend with palette */}
                                    <div
                                        aria-hidden
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            background:
                                                "linear-gradient(180deg, rgba(14,42,35,0) 55%, rgba(14,42,35,0.35) 100%)," +
                                                "linear-gradient(135deg, rgba(95,168,138,0.08) 0%, rgba(212,181,66,0.08) 100%)",
                                        }}
                                    />
                                </div>

                                {/* Floating impact chip */}
                                <div
                                    className="absolute -bottom-4 -left-4 rounded-full px-4 py-2.5 text-white text-xs font-medium float-y"
                                    style={{
                                        background: "linear-gradient(135deg, #0E2A23 0%, #235F52 100%)",
                                        boxShadow: "0 16px 34px -14px rgba(35,95,82,0.55)",
                                    }}
                                >
                                    <i className="fa-solid fa-arrow-trend-up mr-1.5" style={{ color: "#C9F27E" }} />
                                    50-60% inbound auto-resolved
                                </div>

                                {/* Floating tiny glass card */}
                                <div
                                    className="absolute -top-3 -right-3 rounded-2xl bg-white/85 backdrop-blur-md border border-white/70 px-3.5 py-2.5 float-y"
                                    style={{
                                        boxShadow: "0 20px 40px -20px rgba(35,95,82,0.30)",
                                        animationDelay: "1.1s",
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="w-8 h-8 rounded-lg grid place-items-center text-white"
                                            style={{ background: "linear-gradient(135deg,#25D366,#128C7E)" }}
                                        >
                                            <i className="fa-brands fa-whatsapp text-sm" />
                                        </span>
                                        <div>
                                            <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 leading-none">Live now</div>
                                            <div className="text-xs font-semibold text-slate-800 leading-tight mt-0.5">Nyra Assist</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* STORIES */}
            <CustomerStoriesSection openDemo={openDemo} hideHeader />

            {/* CTA */}
            <section className="py-24 md:py-28 bg-cta-glow">
                <div className="container-x text-center max-w-3xl mx-auto">
                    <Reveal>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-6xl">
                            Your story could be <span className="italic">next</span>.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <button
                                onClick={openDemo}
                                className="pill-dark"
                                data-testid="stories-cta-book"
                            >
                                Book a demo
                            </button>
                            <Link to="/use-cases" className="pill-ghost">
                                Explore use cases
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
